import React, { useRef, useState } from "react";
import { MdDelete } from "react-icons/md";
import AlertMessage from "../../../AlertMessage/AlertMessage";
import ConfirmPopup from "../../../ConfirmPopup/ConfirmPopup";
import { Tooltip } from "../../../CommonUI/tooltip";
import { DeleteInvoice } from "../../../../Services/InvoiceService/InvoiceService";

interface DeleteButtonProps {
  invoiceIds: number[];
  onSuccess: () => void;
  onBeforeAction?: (action: () => void) => void;
}

const DeleteButton: React.FC<DeleteButtonProps> = ({
  invoiceIds,
  onSuccess,
  onBeforeAction,
}) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hover, setHover] = useState(false);
  const submissionInProgress = useRef(false);

  const count = invoiceIds.length;

  const submitDelete = async () => {
    if (submissionInProgress.current) return;
    if (count === 0) {
      AlertMessage({
        type: "error",
        text: "Something went wrong. The file could not be deleted. Please try again.",
      });
      return;
    }

    submissionInProgress.current = true;
    setIsSubmitting(true);
    try {
      await Promise.all(invoiceIds.map((id) => DeleteInvoice(id)));
      setOpen(false);
      onSuccess();
    } catch (error) {
      console.error("Failed to delete supplier statement:", error);
      AlertMessage({
        type: "error",
        text: "Something went wrong. The file(s) could not be deleted. Please try again.",
      });
    } finally {
      submissionInProgress.current = false;
      setIsSubmitting(false);
    }
  };

  const confirmMessage =
    count === 1
      ? "Are you sure you want to delete this file? This action cannot be undone."
      : `Are you sure you want to delete ${count} files? This action cannot be undone.`;

  return (
    <>
      <div style={{ marginRight: "20px" }}>
        <Tooltip title="Delete This Invoice" description="">
          <button
            aria-label="Delete supplier statement"
            style={{
              backgroundColor: hover ? "#f0f0f0" : "transparent",
              border: "none",
              padding: "6px",
              borderRadius: "4px",
              cursor: "pointer",
              transition: "all 0.3s ease",
              transform: hover ? "scale(1.2)" : "scale(1)",
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onClick={() => {
              if (onBeforeAction) onBeforeAction(() => setOpen(true));
              else setOpen(true);
            }}
          >
            <MdDelete 
            size={22} 
            style={{ color: hover ? "red" : "darkred" }} 
            />
          </button>
        </Tooltip>
      </div>

      <ConfirmPopup
        blur
        isOpen={open}
        title="Delete Supplier Statement"
        message={confirmMessage}
        confirmLabel={isSubmitting ? "Deleting..." : "Confirm Delete"}
        cancelLabel="Cancel"
        confirmColor="#e53e3e"
        onConfirm={() => void submitDelete()}
        onCancel={() => setOpen(false)}
        confirmDisabled={isSubmitting}
      />
    </>
  );
};

export default DeleteButton;
